import { useContext } from 'react'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import { launchModal, mergeAndStringifyEntityObjects } from '@domain-group/javascript-utils'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as orderBundleItemActions from '@redux/modules/orderBundleItem'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

import defaultState, { requiredFields } from '@models/orderBundleItem'
import type { OrderBundleItemModel, ProviderModel, ProviderPriceListModel, PublicationModel } from '@models/types'

const stringifyArrayKeys = ['options']

type CreateOrderBundleItemParams = {
  dispatch: AppDispatch,
  orderBundleItemParams: Partial<OrderBundleItemModel>,
  requestOptions?: EntityOptions,
}

const createOrderBundleItem = (params: CreateOrderBundleItemParams) => {
  const { orderBundleItemParams, dispatch, requestOptions } = params
  const { createOrderBundleItem: createFn } = orderBundleItemActions

  const updatedParams = mergeAndStringifyEntityObjects({}, orderBundleItemParams, [], stringifyArrayKeys)
  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateOrderBundleItemPayload = {
  dispatch: AppDispatch,
  orderBundleItem: OrderBundleItemModel,
  orderBundleItemParams: Partial<OrderBundleItemModel>,
  requestOptions?: EntityOptions,
}

const updateOrderBundleItem = (params: UpdateOrderBundleItemPayload) => {
  const {
    orderBundleItem, orderBundleItemParams, dispatch, requestOptions,
  } = params

  const { updateOrderBundleItem: updateFn } = orderBundleItemActions

  const updatedParams = mergeAndStringifyEntityObjects(orderBundleItem, orderBundleItemParams, [], stringifyArrayKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

const deleteOrderBundleItem = (params: { orderBundleItem: Partial<OrderBundleItemModel>, dispatch: AppDispatch }) => {
  const { orderBundleItem, dispatch } = params
  const { deleteOrderBundleItem: deleteFn } = orderBundleItemActions

  // @ts-expect-error TS2345
  return dispatch(deleteFn(orderBundleItem))
}

export function useOrderBundleItemForm(
  orderBundleItem: Partial<OrderBundleItemModel> = {},
  options: UseFormOptions = {},
): UseFormPayload {
  const { validateOn } = options

  const orderBundleItemForm = useForm(defaultState, { entity: orderBundleItem, requiredFields, validateOn }, [
    orderBundleItem.id,
    orderBundleItem.cache_key,
  ])

  return {
    ...orderBundleItemForm,
  }
}

export function useRelations(orderBundleItem: OrderBundleItemModel) {
  const { provider_id, provider_price_list_id, publication_id } = orderBundleItem || {}

  const { providers, providerPriceLists, publications } = useSelector(reduxState => reduxState.entities)

  const provider: Partial<ProviderModel> = provider_id ? providers[provider_id] || {} : {}
  const providerPriceList: Partial<ProviderPriceListModel> = provider_price_list_id
    ? providerPriceLists[provider_price_list_id] || {} : {}
  const publication: Partial<PublicationModel> = publication_id ? publications[publication_id] || {} : {}

  return {
    provider,
    providerPriceList,
    publication,
  }
}

function useOrderBundleItem(initEntity: Partial<OrderBundleItemModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: orderBundleItem } = useLatestEntity(initEntity, 'orderBundleItems')

  const dispatch = useThunkDispatch()

  const {
    creating, deleting, updating, loading,
  } = useSelector(reduxState => reduxState.orderBundleItems)

  const { callbacks } = useContext(PageContext)

  // @ts-expect-error TS2345
  const { provider, providerPriceList, publication } = useRelations(orderBundleItem)

  return {
    callbacks: {
      createOrderBundleItem: (orderBundleItemParams: Partial<OrderBundleItemModel>, requestOptions: EntityOptions) => (
        createOrderBundleItem({ orderBundleItemParams, dispatch, requestOptions })
      ),
      // @ts-expect-error TS2322
      deleteOrderBundleItem: () => deleteOrderBundleItem({ orderBundleItem, dispatch }),
      createOrEditOrderBundleItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditOrderBundleItemModal',
        payload: { orderBundleItem },
      }),
      updateOrderBundleItem: (orderBundleItemParams: Partial<OrderBundleItemModel>, requestOptions: EntityOptions) => (
        updateOrderBundleItem({
          // @ts-expect-error TS2322
          orderBundleItem, orderBundleItemParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    deleting,
    loading,
    orderBundleItem,
    provider,
    providerPriceList,
    publication,
    updating,
  }
}

export default useOrderBundleItem
